import { mediaQueryLarge, requestIdleCallback, startViewTransition } from '@theme/utilities';
import PaginatedList from '@theme/paginated-list';

const QUICK_ORDER_QUERY_PARAM = 'q_search';

export default class ResultsList extends PaginatedList {
  /** @type {string[]} */
  fullCardsHTML = [];

  /** @type {{ html: string; text: string }[] | null} */
  cardSearchData = null;

  /** @type {string} */
  searchQuery = '';

  /** @type {string} */
  searchRawQuery = '';

  /** @type {string[] | null} */
  filteredCardsHTML = null;

  connectedCallback() {
    super.connectedCallback();

    const persistedQuery = new URL(window.location.href).searchParams.get(QUICK_ORDER_QUERY_PARAM);
    if (persistedQuery) {
      this.searchRawQuery = persistedQuery;
      this.searchQuery = persistedQuery.trim().toLowerCase();
    }

    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange);
    this.setAttribute('initialized', '');

    requestIdleCallback(() => {
      this.#setupQuickOrderSearch();
    });
  }

  disconnectedCallback() {
    // Ensure base cleanup (including filter listeners) still runs.
    super.disconnectedCallback();
    mediaQueryLarge.removeEventListener('change', this.#handleMediaQueryChange);
  }

  handleFilterUpdate() {
    const shouldReapplySearch = Boolean(this.searchQuery);

    this.filteredCardsHTML = null;
    this.fullCardsHTML = [];
    this.cardSearchData = null;
    if (this.#searchDebounceTimer !== null) {
      clearTimeout(this.#searchDebounceTimer);
      this.#searchDebounceTimer = null;
    }

    super.handleFilterUpdate();

    this.#scrollToMain();

    if (shouldReapplySearch) {
      requestIdleCallback(() => {
        this.#applySearchQuery(this.searchRawQuery, { scroll: false }).catch(() => {});
      });
    } else {
      this.#syncSearchParam('');
      this.#syncSearchInputs('');
    }
  }

  #getCurrentSource() {
    /** @type {string[]} */
    let source;
    if (this.searchQuery) {
      if (Array.isArray(this.filteredCardsHTML)) {
        source = this.filteredCardsHTML;
      } else {
        source = [];
      }
    } else if (Array.isArray(this.fullCardsHTML) && this.fullCardsHTML.length > 0) {
      source = this.fullCardsHTML;
    } else {
      source = this.allCardsHTML || [];
    }

    return source;
  }

  #ensureCardSearchData() {
    if (this.cardSearchData && this.cardSearchData.length === this.allCardsHTML.length) {
      return;
    }

    this.fullCardsHTML = Array.isArray(this.allCardsHTML) ? [...this.allCardsHTML] : [];
    /** @type {{ html: string; text: string }[]} */
    const data = [];

    const template = document.createElement('template');
    for (const html of this.fullCardsHTML) {
      template.innerHTML = html.trim();
      const li = template.content.firstElementChild;
      let text = '';
      if (li) {
        const nameEl = li.querySelector('.list-view-name') || li;
        const nameText = (nameEl.textContent || '').toLowerCase().trim();

        /** @type {string[]} */
        const skuParts = [];
        li.querySelectorAll('spark-product-card').forEach((card) => {
          if (!(card instanceof HTMLElement)) return;
          const sku = (card.getAttribute('data-sku') || card.getAttribute('sku') || '').trim();
          if (sku) {
            skuParts.push(sku.toLowerCase());
          }
        });

        text = [nameText, ...skuParts].join(' ').trim();
      }
      data.push({ html, text });
    }

    this.cardSearchData = data;
  }

  #setupQuickOrderSearch() {
    /** @type {NodeListOf<HTMLInputElement>} */
    const inputs = this.#getSearchInputs();
    if (!inputs || inputs.length === 0) return;

    const clearButtons = document.querySelectorAll('[data-quick-order-search-clear]');

    /**
     * @param {HTMLInputElement} input
     */
    const updateClearButtonVisibility = (input) => {
      const wrapper = input.closest('.quick-order-search-input-wrapper');
      if (!wrapper) return;
      const clearButton = wrapper.querySelector('[data-quick-order-search-clear]');
      if (clearButton instanceof HTMLElement) {
        clearButton.style.display = input.value.trim() ? 'flex' : 'none';
      }
    };

    inputs.forEach((input) => {
      input.addEventListener('input', () => {
        updateClearButtonVisibility(input);
        if (this.#searchDebounceTimer !== null) {
          clearTimeout(this.#searchDebounceTimer);
        }
        this.#searchDebounceTimer = window.setTimeout(() => {
          this.#applySearchQuery(input.value || '', { sourceInput: input }).catch(() => {});
        }, 200);
      });

      input.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') {
          this.#applySearchQuery('', { sourceInput: null, scroll: false }).catch(() => {});
        }
      });
    });

    // Handle clear button clicks
    clearButtons.forEach((button) => {
      button.addEventListener('click', () => {
        const wrapper = button.closest('.quick-order-search-input-wrapper');
        if (!wrapper) return;
        const input = wrapper.querySelector('[data-quick-order-search-input]');
        if (input instanceof HTMLInputElement) {
          this.#applySearchQuery('', { sourceInput: null, scroll: false }).catch(() => {});
        }
      });
    });

    if (this.searchRawQuery) {
      inputs.forEach((input) => {
        input.value = this.searchRawQuery;
        updateClearButtonVisibility(input);
      });
      this.#applySearchQuery(this.searchRawQuery, { scroll: false }).catch(() => {});
    } else {
      // Hide clear buttons initially if no query
      inputs.forEach((input) => {
        updateClearButtonVisibility(input);
      });
    }
  }

  /** @type {number | null} */
  #searchDebounceTimer = null;

  /**
   * @param {{ target: EventTarget | null }} params
   */
  updateLayout({ target }) {
    if (!(target instanceof HTMLInputElement)) return;

    this.#animateLayoutChange(target.value);
  }

  /**
   * @param {string} value
   */
  #animateLayoutChange = async (value) => {
    const { grid } = this.refs;

    if (!grid) return;

    await startViewTransition(() => this.#setLayout(value), ['product-grid']);

    requestIdleCallback(() => {
      const viewport = mediaQueryLarge.matches ? 'desktop' : 'mobile';
      sessionStorage.setItem(`product-grid-view-${viewport}`, value);
    });
  };

  /**
   * @param {string} value
   */
  #setLayout(value) {
    const { grid } = this.refs;
    if (!grid) return;
    grid.setAttribute('product-grid-view', value);
  }

  /**
   * @param {number} page
   */
  async goToPage(page) {
    if (!this.searchQuery) {
      return super.goToPage(page);
    }

    const { grid } = this.refs;
    if (!grid) return;

    await this.buildAllCardsIfNeeded();

    const source = this.#getCurrentSource();
    const pageSize = parseInt(grid.dataset.pageSize || '16', 10);
    const totalPages = source.length > 0 ? Math.max(1, Math.ceil(source.length / pageSize)) : 1;

    if (page < 1 || page > totalPages) return;

    grid.style.opacity = '0.5';
    grid.style.pointerEvents = 'none';

    try {
      this.renderClientPage(page);
      this.updateProductCount();

      const cleanUrl = new URL(window.location.href);
      cleanUrl.searchParams.set('page', page.toString());
      cleanUrl.searchParams.delete('section_id');
      cleanUrl.hash = '';
      history.pushState('', '', cleanUrl.toString());

      this.#scrollToMain();

      if (this.aspectRatioHelper) {
        this.aspectRatioHelper.processNewElements();
      }
    } finally {
      grid.style.opacity = '';
      grid.style.pointerEvents = '';
    }
  }

  /**
   * @param {number | undefined} [totalPagesOverride]
   */
  renderPaginationControls(totalPagesOverride) {
    super.renderPaginationControls(totalPagesOverride);

    const { grid, paginationContainer } = this.refs;
    if (!grid || !paginationContainer) return;

    const totalPages =
      typeof totalPagesOverride === 'number' ? totalPagesOverride : parseInt(grid.dataset.lastPage || '1', 10);

    if (this.searchQuery && totalPages <= 1) {
      paginationContainer.innerHTML = '';
    }
  }

  updateProductCount() {
    const countElements = document.querySelectorAll('.products-count');
    if (countElements.length === 0) return;
    const { grid } = this.refs;
    if (!grid) return;

    const cardsOnPage = grid.querySelectorAll('[ref="cards[]"]').length;
    const itemsOnPage = cardsOnPage;

    const source = this.#getCurrentSource();
    const visibleTotal =
      Array.isArray(source) && source.length > 0 ? source.length : parseInt(grid.dataset.totalItems || '0', 10);

    const newText = `Showing ${itemsOnPage} of ${visibleTotal} items`;

    countElements.forEach((element) => {
      element.innerHTML = newText;
    });
  }

  /**
   * @param {number} page
   */
  renderClientPage(page) {
    const { grid } = this.refs;
    if (!grid) return;

    if (!this.searchQuery) {
      super.renderClientPage(page);
      return;
    }

    const source = this.#getCurrentSource();

    if (!Array.isArray(source) || source.length === 0) {
      if (this.searchQuery) {
        const safeQuery = this.searchQuery.replace(/[&<>"']/g, (ch) => {
          switch (ch) {
            case '&':
              return '&amp;';
            case '<':
              return '&lt;';
            case '>':
              return '&gt;';
            case '"':
              return '&quot;';
            case "'":
              return '&#39;';
            default:
              return ch;
          }
        });

        grid.innerHTML = `
          <li class="list-view-row list-view-row--empty" role="listitem">
            <div class="list-view-item list-view-item--empty">
              <p>No results found for "${safeQuery}".</p>
            </div>
          </li>
        `;
        this.renderPaginationControls(1);
      }
      return;
    }

    const pageSize = parseInt(grid.dataset.pageSize || '16', 10);
    const totalPages = Math.max(1, Math.ceil(source.length / pageSize));

    let targetPage = page;
    if (targetPage < 1 || targetPage > totalPages) {
      targetPage = 1;
    }

    this.currentPage = targetPage;

    const startIndex = (targetPage - 1) * pageSize;
    const endIndex = Math.min(startIndex + pageSize, source.length);

    const cardsToRender = source.slice(startIndex, endIndex);
    const pageHTML = cardsToRender.join('');

    grid.innerHTML = pageHTML;

    this.renderPaginationControls(totalPages);
  }

  /**
   * @param {number} [delayMs]
   */
  #scrollToMain(delayMs = 150) {
    const scrollFn = () => {
      /** @type {(Element | null | undefined)[]} */
      const candidates = [
        document.scrollingElement,
        document.querySelector('#MainContent'),
        document.querySelector('main'),
        document.documentElement,
        document.body,
      ];

      candidates.forEach((el) => {
        if (!(el instanceof HTMLElement)) return;
        if (typeof el.scrollTo === 'function') {
          el.scrollTo(0, 0);
        }
        el.scrollTop = 0;
      });

      window.scrollTo(0, 0);
    };

    if (delayMs > 0) {
      window.setTimeout(() => {
        window.requestAnimationFrame(scrollFn);
      }, delayMs);
    } else {
      window.requestAnimationFrame(scrollFn);
    }
  }

  /**
   * Handles the media query change event.
   *
   * @param {MediaQueryListEvent} event
   */
  #handleMediaQueryChange = (event) => {
    const targetElement = event.matches
      ? this.querySelector('[data-grid-layout="desktop-default-option"]')
      : this.querySelector('[data-grid-layout="mobile-option"]');

    if (!(targetElement instanceof HTMLInputElement)) return;

    targetElement.checked = true;
    this.#setLayout('default');
  };

  /**
   * @returns {NodeListOf<HTMLInputElement>}
   */
  #getSearchInputs() {
    const local = /** @type {NodeListOf<HTMLInputElement>} */ (
      this.querySelectorAll('[data-quick-order-search-input]')
    );
    if (local && local.length > 0) {
      return local;
    }
    return /** @type {NodeListOf<HTMLInputElement>} */ (document.querySelectorAll('[data-quick-order-search-input]'));
  }

  /**
   * @param {string} rawQuery
   * @param {HTMLInputElement | null} [sourceInput=null]
   */
  #syncSearchInputs(rawQuery, sourceInput = null) {
    const inputs = this.#getSearchInputs();
    if (!inputs) return;

    inputs.forEach((input) => {
      if (!(input instanceof HTMLInputElement)) return;
      if (sourceInput && input === sourceInput) return;
      input.value = rawQuery;

      const wrapper = input.closest('.quick-order-search-input-wrapper');
      if (wrapper) {
        const clearButton = wrapper.querySelector('[data-quick-order-search-clear]');
        if (clearButton instanceof HTMLElement) {
          clearButton.style.display = rawQuery.trim() ? 'flex' : 'none';
        }
      }
    });
  }

  /**
   * @param {string} rawQuery
   */
  #syncSearchParam(rawQuery) {
    const url = new URL(window.location.href);

    if (rawQuery) {
      url.searchParams.set(QUICK_ORDER_QUERY_PARAM, rawQuery);
    } else {
      url.searchParams.delete(QUICK_ORDER_QUERY_PARAM);
    }

    history.replaceState({}, '', url.toString());
  }

  /**
   * @param {string} rawQuery
   * @param {{ sourceInput?: HTMLInputElement | null; scroll?: boolean }} [options]
   */
  async #applySearchQuery(rawQuery, options = {}) {
    const { sourceInput = null, scroll = true } = options;
    const query = (rawQuery || '').trim().toLowerCase();

    this.searchQuery = query;
    this.searchRawQuery = rawQuery || '';

    this.setLoadingState(true);

    await this.buildAllCardsIfNeeded();

    if (!query) {
      this.filteredCardsHTML = null;
    } else {
      this.#ensureCardSearchData();
      if (this.cardSearchData) {
        const matched = this.cardSearchData.filter((item) => item.text.includes(query)).map((item) => item.html);
        this.filteredCardsHTML = Array.from(new Set(matched));
      }
    }

    this.currentPage = 1;
    this.renderClientPage(1);
    this.updateProductCount();
    this.setProductCountVisibility(true);
    this.setLoadingState(false);

    if (scroll) {
      this.#scrollToMain();
    }

    this.#syncSearchInputs(this.searchRawQuery, sourceInput);
    this.#syncSearchParam(this.searchRawQuery);
  }
}

if (!customElements.get('results-list')) {
  customElements.define('results-list', ResultsList);
}
